import {
  FooterVariant,
  InlineNotification,
  InlineNotificationVariant,
  PromptVariant,
  TabBar,
} from "@skylead/component-library"
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js"
import moment from "moment"
import React from "react"
import { Button } from "react-bootstrap"
import { useDispatch } from "react-redux"
import { PaymentIcon } from "react-svg-credit-card-payment-icons"

import InfoBox from "../components/atoms/InfoBox"
import Modal, { ModalVariant } from "../components/atoms/Modal"
import ModalSettingsOptionCard from "../components/atoms/ModalSettingsOptionCard"
import InputGroup from "../components/organisms/InputGroup"
import * as actions from "../redux/actions"
import { store } from "../redux/store"
import {
  getUserType,
  hasPermission,
  PERMISSIONS,
  USER_TYPES,
} from "./team-management/team-management-utils"
import { validateCardName, validateChangeAccountName } from "./validation/validation-utils"
import { hideBilling, isWhiteLabelNoAdmin } from "./whitelabel-utils"

export function AccountStatusItemListModals(
  tt,
  user,
  selectedAccount,
  accountProxy,
  countries,
  savedCard,
  setState,
  accountSpecificPrices,
  pricingPlans,
  availableSeats,
  allCancellationReasons,
  setCancellationReasons,
  cancellationReasons,
  canManagePayment,
  state,
) {
  const dispatch = useDispatch()

  const {
    showAccountSettings,
    deleteAccountModal,
    showPaymentNeededModal,
    amountForPaymentNeeded,
    showSavedCard,
    cancelSubscriptionModal,
  } = state

  const reactivateAccount = async () => {
    if (selectedAccount.accountPaymentStatusId === 2) {
      setState({
        openReactivateProxyModal: true,
        showConfirmModal: false,
        showAccountSettings: false,
      })
    } else {
      dispatch(actions.reactivateAccount(tt, selectedAccount.id))
      setState({
        showConfirmModal: false,
        showAccountSettings: false,
      })
    }
  }

  const clearPlanSeatsFields = () => {
    pricingPlans.forEach(plan => {
      dispatch(actions.updateFormField(`${plan.name}-seats`, 0))
    })
  }

  const reactivateAccountModal = () => {
    const { currentPlanId } = accountSpecificPrices
    const currentPlanName = pricingPlans.find(plan => plan.id === currentPlanId)?.name
    const availableSeatsForCurrentPlan = availableSeats.find(
      seat => seat.planId === currentPlanId,
    ).quantity
    if (availableSeatsForCurrentPlan > 0) {
      dispatch(
        actions.showPrompt(
          PromptVariant.WARNING,
          tt("reactivate-account"),
          tt("reactivate-account-modal.prompt.msg", { action: currentPlanName }),
          reactivateAccount,
          tt("submit"),
        ),
      )
    } else {
      dispatch(
        actions.showPrompt(
          PromptVariant.INFO,
          tt("reactivate-account"),
          <>
            <p>{tt("reactivate-account-modal.seat.prompt.msg1", { action: currentPlanName })}</p>

            <span>
              {tt("reactivate-account-modal.seat.prompt.msg2")}{" "}
              <span
                onClick={() => {
                  clearPlanSeatsFields()
                  setState({
                    showBuySeatsModal: true,
                  })
                }}
              >
                {tt("reactivate-account-modal.seat.prompt.msg3")}
              </span>
            </span>
          </>,
        ),
      )
    }
  }

  const handleSubmitAccountNameChange = async event => {
    event.preventDefault()
    event.stopPropagation()
    const { newAccountName } = store.getState().forms.formData

    dispatch(actions.clearFormErrors())
    const allErrors = validateChangeAccountName({ newAccountName })
    dispatch(actions.setFormErrors(allErrors))

    if (allErrors) return false

    await dispatch(actions.changeAccountName(tt, newAccountName))
    setState({ showAccountSettings: false })
  }

  const showChangeAccountNameModal = () => {
    dispatch(
      actions.showModal(
        ModalVariant.SMALL,
        tt("change-seat-name"),
        <InputGroup
          field="newAccountName"
          label={tt("seat-name")}
          type="text"
          placeholder={tt("change-account-name.seat-name.placeholder")}
          inputGroupClassName="input-on-surface"
        />,
        FooterVariant.TRANSACTIONAL,
        { label: tt("save"), onClick: handleSubmitAccountNameChange },
        { label: tt("cancel") },
        undefined,
        undefined,
        true,
      ),
    )
  }

  const handleSubmitAccountTimeZoneChange = async () => {
    const { selectedTimezone } = store.getState().forms.formData

    await dispatch(
      actions.changeTimezone(tt, {
        timeZone: selectedTimezone.name,
        startUTCTime: "09:00",
        endUTCTime: "17:00",
      }),
    )
    setState({ showAccountSettings: false })
  }

  const showChangeSeatTimeZone = async () => {
    await dispatch(actions.getTimezone())
    dispatch(
      actions.showModal(
        ModalVariant.SMALL,
        tt("change-seat-time-zone"),
        <div className="wrapper-timezone">
          <InputGroup
            field="selectedTimezone"
            type="timezone"
            label={tt("your-time-zone")}
            inputGroupClassName="input-on-surface"
            inputClassName="timezone-picker-seat-settings"
            inputProps={{
              placeholder: tt("change-seat-time.timezone.placeholder"),
              name: "timezone",
            }}
          />
          <InfoBox className="timezone-info-box">{tt("change-seat-time-zone.text")}</InfoBox>
        </div>,
        FooterVariant.TRANSACTIONAL,
        { label: tt("submit-changes"), onClick: handleSubmitAccountTimeZoneChange },
        { label: tt("cancel") },
        undefined,
        undefined,
        true,
      ),
    )
  }

  const handleReasonsModalClose = async modalId => {
    setState({ [modalId]: false, showAccountSettings: true })
    setCancellationReasons([])
    dispatch(actions.clearForm())
    dispatch(actions.clearFormErrors())
  }

  const renderCancellationReasons = () => (
    <ul className="account-cancellation-reasons">
      {allCancellationReasons.items.map(reason => {
        const isReasonChecked = cancellationReasons.find(
          cancellationReason => cancellationReason.id === reason.id,
        )
        return (
          <li key={reason.id}>
            <input
              type="checkbox"
              id={`step-${reason.id}`}
              checked={isReasonChecked}
              onChange={() => {
                if (isReasonChecked) {
                  setCancellationReasons(
                    cancellationReasons.filter(
                      cancellationReason => cancellationReason.id !== reason.id,
                    ),
                  )
                } else {
                  setCancellationReasons([...cancellationReasons, reason])
                }
              }}
            />
            <label htmlFor={`step-${reason.id}`}>{reason.name}</label>
            {reason.commentRequired && isReasonChecked && (
              <InputGroup
                bordered
                field={reason.id}
                placeholder={tt("placeholder.leave.comment")}
                type="textarea"
                useEnterAsDefault
                inputProps={{ rows: 3 }}
              />
            )}
          </li>
        )
      })}
    </ul>
  )

  const handleReasonsSubmit = async () => {
    const { formData } = store.getState().forms
    let cancellationReasonsValid = true
    if (cancellationReasons.length === 0) {
      cancellationReasonsValid = false
      dispatch(
        actions.showPrompt(
          PromptVariant.INFO,
          tt("warning"),
          tt("account.statement.list.reason-submit.no-input.prompt.msg"),
        ),
      )
    } else {
      cancellationReasons.forEach(cancellationReason => {
        if (cancellationReason.commentRequired && !formData[cancellationReason.id]) {
          cancellationReasonsValid = false
          dispatch(
            actions.setFormErrors({
              [cancellationReason.id]: tt("account.statement.list.cancel.reason.error"),
            }),
          )
        }
      })
    }
    if (cancellationReasonsValid) {
      const reason = cancellationReasons
        .map(cancellationReason => {
          if (cancellationReason.commentRequired) {
            return `${cancellationReason.name}: ${formData[cancellationReason.id]}`
          }
          return cancellationReason.name
        })
        .join("\n")
      const callback = () => {
        setState({
          showConfirmModal: false,
          showAccountSettings: false,
          cancelSubscriptionModal: false,
          deleteAccountModal: false,
        })
        setCancellationReasons([])
      }
      await dispatch(
        deleteAccountModal
          ? actions.deleteSeat(tt, reason, callback)
          : actions.cancelSubscription(tt, reason, callback),
      )
    }
  }

  const stripe = useStripe()
  const elements = useElements()

  const payAccount = async (stripeToken, allErrors) => {
    let newAllErrors = { ...allErrors }

    const successfullyBought = dispatch(
      actions.paySubscription(tt, stripeToken, selectedAccount.subscription.id),
    )

    if (successfullyBought === true) {
      setState({ showPaymentNeededModal: false })
    } else if (successfullyBought) {
      const cardSecurityReps = await stripe.confirmCardPayment(successfullyBought)
      if (cardSecurityReps.error) {
        newAllErrors = { ...allErrors, card: cardSecurityReps.error.message }
      } else if (cardSecurityReps.paymentIntent) {
        setState({ showPaymentNeededModal: false })
      }
    }
    return newAllErrors
  }

  const submitPaymentNeeded = async event => {
    event.preventDefault()
    event.stopPropagation()

    const cardName = store.getState().forms.formData.cardName || savedCard.cardInfo?.name

    dispatch(actions.clearFormErrors())
    let allErrors = {}
    if (showSavedCard) {
      allErrors = await payAccount(undefined, allErrors)
    } else {
      allErrors = validateCardName({ cardName })
      if (allErrors) {
        dispatch(actions.setFormErrors(allErrors))
        return false
      }

      const card = elements.getElement(CardElement)
      const stripeResp = await stripe.createToken(card, {
        name: cardName,
      })
      if (stripeResp.token) {
        allErrors = await payAccount(stripeResp.token.id, allErrors)
      } else {
        allErrors = { ...allErrors, card: stripeResp.error.message }
      }
    }
    dispatch(actions.setFormErrors(allErrors))

    if (allErrors) return false
  }

  const handleModalState = (stateType, stateValue) => {
    setState({ [stateType]: stateValue })
  }

  const handleNavState = (event, navType) => {
    setState({ [navType]: event === "1" })
  }

  const handleReasonsModalOpen = modalId => {
    setState({ showAccountSettings: false, [modalId]: true })
  }

  const paySubscriptionTabBarItems = [
    {
      canView: true,
      label: tt("saved-card"),
      isActive: showSavedCard,
      onClick: () => handleNavState("1", "showSavedCard"),
    },
    {
      canView: true,
      label: tt("new-card"),
      isActive: !showSavedCard,
      onClick: () => handleNavState("2", "showSavedCard"),
    },
  ]

  return (
    <div>
      {showAccountSettings && (
        <Modal
          show={showAccountSettings}
          type={ModalVariant.SMALL}
          title={
            [7, 8].includes(selectedAccount.accountGlobalStatusId)
              ? tt("subscription-not-active")
              : tt("subscription.active")
          }
          content={
            <>
              {selectedAccount.accountGlobalStatusId === 8 && (
                <>
                  <Button center onClick={reactivateAccountModal}>
                    {tt("reactivate-account")}
                  </Button>
                  <br />
                </>
              )}

              {hasPermission(PERMISSIONS.SEAT_SETTINGS_EDIT) && (
                <ModalSettingsOptionCard
                  icon="change-account-name"
                  text={tt("change-seat-name")}
                  onClick={showChangeAccountNameModal}
                />
              )}

              {hasPermission(PERMISSIONS.SEAT_SETTINGS_EDIT) && (
                <ModalSettingsOptionCard
                  icon="change-seat-time-zone"
                  text={tt("change-seat-time-zone")}
                  onClick={showChangeSeatTimeZone}
                />
              )}

              {hasPermission(PERMISSIONS.CANCEL_SUBSCRIPTION_EDIT) &&
                !!selectedAccount.subscription &&
                selectedAccount.accountGlobalStatusId !== 8 && (
                  <ModalSettingsOptionCard
                    icon="cancel-subscription"
                    text={tt("cancel-subscription")}
                    onClick={() => {
                      handleReasonsModalOpen("cancelSubscriptionModal")
                    }}
                  />
                )}

              {hasPermission(PERMISSIONS.DELETE_ACCOUNT_EDIT) && (
                <ModalSettingsOptionCard
                  icon="delete-account"
                  text={tt("delete-seat")}
                  onClick={() => {
                    handleReasonsModalOpen("deleteAccountModal")
                  }}
                />
              )}

              <br />
              {selectedAccount.email && (
                <p className="text-center">
                  {tt("acccount-status-item-list.selected-email", { email: selectedAccount.email })}
                </p>
              )}
              <br />
              <p className="text-center">
                {tt("label.proxy-location")}{" "}
                {accountProxy
                  ? accountProxy.proxyCountryCode
                    ? countries.getName(accountProxy.proxyCountryCode.toUpperCase(), "en")
                    : tt("custom")
                  : tt("none")}
              </p>
              {!isWhiteLabelNoAdmin() && !hideBilling() && selectedAccount.subscription && (
                <>
                  <br />
                  <p className="text-center">
                    {tt("label.subscription-active-until")}{" "}
                    {moment
                      .unix(selectedAccount.subscription.subscriptionEndsAt)
                      .format("DD.MM.YYYY.")}
                  </p>
                  {![7, 8].includes(selectedAccount.accountGlobalStatusId) && (
                    <p className="text-center">
                      {tt("label.subscription-renewal-data")}{" "}
                      {moment
                        .unix(selectedAccount.subscription.subscriptionEndsAt)
                        .format("DD.MM.YYYY.")}
                    </p>
                  )}
                  <br />
                </>
              )}
            </>
          }
          footerVariant={FooterVariant.ACKNOWLEDGMENT}
          primaryBtn={{
            label: tt("cancel"),
            onClick: () => handleModalState("showAccountSettings", false),
          }}
        />
      )}

      {deleteAccountModal && (
        <Modal
          show={deleteAccountModal}
          type={ModalVariant.SMALL}
          title={tt("delete-seat")}
          content={
            <div id="delete-account">
              {tt("delete-seat.msg")}
              {renderCancellationReasons()}
              <hr />
              <InlineNotification
                type={InlineNotificationVariant.WARNING}
                label={tt("delete-seat.text")}
              />
            </div>
          }
          footerVariant={FooterVariant.TRANSACTIONAL_DESTRUCTIVE}
          primaryBtn={{ label: tt("submit"), onClick: handleReasonsSubmit }}
          secondaryBtn={{
            label: tt("cancel"),
            onClick: () => {
              handleReasonsModalClose("deleteAccountModal")
            },
          }}
          noHideOnSubmit
        />
      )}

      {showPaymentNeededModal && (
        <Modal
          show={showPaymentNeededModal}
          type={ModalVariant.SMALL}
          title={tt("pay-subscription")}
          content={
            canManagePayment ? (
              <>
                {savedCard.hasCard && <TabBar items={paySubscriptionTabBarItems} />}
                {showSavedCard ? (
                  <>
                    <InputGroup
                      inputGroupClassName="saved-card-input input-on-surface"
                      field="card"
                      label={tt("card-details")}
                      prepend={
                        <PaymentIcon
                          id={savedCard.cardInfo?.brand.toLowerCase()}
                          style={{ width: 21 }}
                          className="payment-icon"
                        />
                      }
                      disabled
                      customValue={`****-****-****-${savedCard.cardInfo?.last4}`}
                      description={savedCard.cardInfo?.brand}
                      placeholder={tt("placeholder.card-number")}
                    />
                    {savedCard.cardInfo?.name && (
                      <InputGroup
                        disabled
                        field="cardName"
                        customValue={savedCard.cardInfo?.name}
                        label={tt("name-on-card")}
                        placeholder={tt("card-name.placeholder")}
                        inputGroupClassName="input-on-surface"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <InputGroup
                      field="card"
                      label={tt("card-details")}
                      type="card"
                      inputGroupClassName="stripe-input-background"
                      placeholder={tt("placeholder.card-number")}
                    />
                    <InputGroup
                      field="cardName"
                      label={tt("name-on-card")}
                      placeholder={tt("card-name.placeholder")}
                      inputGroupClassName="input-on-surface"
                    />
                  </>
                )}
                <br />
                <p className="text-center">{tt("acccount-status.subscription-purchasing")}</p>
                <br />
                <p className="text-center mb-0 font-weight-bold">
                  {tt("acccount-status.checkout-amount", { action: amountForPaymentNeeded })}
                </p>
                <br />
                <p className="text-center font-weight-bold">{tt("powered-by-stripe")}</p>
              </>
            ) : (
              <span>{`${tt("acccount-status.contact.msg1")} ${
                [
                  USER_TYPES.SUPER_ADMIN,
                  USER_TYPES.PLATFORM_CO_OWNER,
                  USER_TYPES.CO_WORKER,
                ].includes(getUserType(user.userTypeId))
                  ? tt("acccount-status.contact.msg2")
                  : tt("acccount-status.contact.msg3")
              } ${tt("acccount-status.contact.msg4")}`}</span>
            )
          }
          footerVariant={FooterVariant.TRANSACTIONAL}
          primaryBtn={{
            label:
              Number(amountForPaymentNeeded) === 0
                ? tt("free-trial")
                : tt("acccount-status.payment-amount", { action: amountForPaymentNeeded }),
            onClick: canManagePayment && submitPaymentNeeded,
          }}
          secondaryBtn={{
            label: tt("cancel"),
            onClick: () => handleModalState("showPaymentNeededModal", false),
          }}
          noHideOnSubmit
        />
      )}

      {cancelSubscriptionModal && (
        <Modal
          show={cancelSubscriptionModal}
          type={ModalVariant.SMALL}
          title={tt("cancel-subscription")}
          content={
            <div id="cancel-subscription">
              {tt("acccount-status.cancel-subscription.text")}
              {renderCancellationReasons()}
              <hr />
              {selectedAccount.subscription && (
                <InlineNotification
                  type={InlineNotificationVariant.WARNING}
                  label={tt("acccount-status.cancel-subscription.msg", {
                    action: moment
                      .unix(selectedAccount.subscription.subscriptionEndsAt)
                      .format("DD/MM/YYYY"),
                  })}
                />
              )}
            </div>
          }
          footerVariant={FooterVariant.TRANSACTIONAL_DESTRUCTIVE}
          primaryBtn={{ label: tt("submit"), onClick: handleReasonsSubmit }}
          secondaryBtn={{
            label: tt("cancel"),
            onClick: () => {
              handleReasonsModalClose("cancelSubscriptionModal")
            },
          }}
          noHideOnSubmit
        />
      )}
    </div>
  )
}
